import type { ChipColor, ChipDot, ChipTone, ChipVariant } from '@/components/Chip/types';
import {
  brand,
  gray,
  green,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
  controlTokens,
  surfaceTokens,
} from '@/theme/themePrimitives';

export const chipSolidTokens: Record<ChipColor, { bg: string; border: string; text?: string }> = {
  info: { bg: controlTokens.info, border: controlTokens.info },
  success: { bg: controlTokens.successHover, border: controlTokens.successHover },
  warning: {
    bg: controlTokens.warningFill,
    border: controlTokens.warningFill,
    text: controlTokens.primary,
  },
  error: { bg: controlTokens.error, border: controlTokens.error },
  ai: { bg: purple[500], border: purple[500] },
  neutral: { bg: gray[600], border: gray[500] },
  dark: { bg: controlTokens.primary, border: controlTokens.primary },
  brand: { bg: controlTokens.cobalt, border: controlTokens.cobalt },
  golden: { bg: yellow[400], border: yellow[400], text: controlTokens.primary },
  whatsapp: {
    bg: controlTokens.whatsapp,
    border: controlTokens.whatsapp,
    text: controlTokens.primary,
  },
  coral: { bg: controlTokens.coral, border: controlTokens.coral, text: controlTokens.primary },
};

export const chipOutlineTokens: Record<ChipColor, { border: string; text: string }> = {
  info: { border: brand[300], text: brand[700] },
  success: { border: green[300], text: green[500] },
  warning: { border: orange[300], text: orange[700] },
  error: { border: red[300], text: red[500] },
  ai: { border: purple[300], text: purple[600] },
  neutral: { border: gray[300], text: gray[700] },
  dark: { border: gray[400], text: gray[900] },
  brand: { border: brand[300], text: brand[700] },
  golden: { border: yellow[300], text: yellow[700] },
  whatsapp: { border: '#86efac', text: '#15803d' },
  coral: { border: controlTokens.coral, text: controlTokens.primary },
};

export const chipTonalTokens: Record<ChipTone, { bg: string; text: string; border: string }> = {
  blue: { bg: brand[100], text: brand[700], border: brand[100] },
  green: { bg: green[100], text: green[500], border: green[200] },
  orange: { bg: orange[100], text: orange[700], border: orange[200] },
  red: { bg: red[100], text: red[500], border: red[200] },
  purple: { bg: purple[100], text: purple[600], border: purple[200] },
  gray: { bg: gray[100], text: gray[700], border: gray[200] },
  yellow: { bg: yellow[100], text: yellow[700], border: yellow[200] },
  teal: { bg: teal[100], text: teal[700], border: teal[200] },
  pink: { bg: pink[100], text: pink[600], border: pink[200] },
};

export const chipDotColors: Record<ChipDot, string> = {
  green: green[400],
  blue: brand[500],
  orange: orange[400],
  red: red[400],
  neutral: gray[400],
};

/** Default entity-avatar fill (admin's `AVATAR_GRADIENTS.brand`; partners has no Avatar utils). */
export const chipAvatarGradient = `linear-gradient(160deg, ${brand[100]}, ${brand[200]})`;

export const chipElevationShadow =
  'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px';

export const chipVariantSx = (
  variant: ChipVariant,
  color: ChipColor,
  tone: ChipTone,
  isAdd: boolean,
  isInteractive: boolean,
) => {
  if (isAdd) {
    return {
      backgroundColor: 'transparent',
      borderColor: gray[300],
      borderStyle: 'dashed',
      color: gray[500],
      '&:hover:not(:disabled)': {
        color: brand[600],
        borderColor: brand[300],
        backgroundColor: brand[50],
      },
    };
  }

  if (variant === 'solid') {
    const t = chipSolidTokens[color];
    return {
      backgroundColor: t.bg,
      ...(color === 'ai' ? { backgroundImage: controlTokens.aiGradient } : {}),
      borderColor: t.border,
      color: t.text ?? surfaceTokens.paper,
      ...(isInteractive && {
        '&:hover:not(:disabled)': { filter: 'brightness(0.95)', boxShadow: chipElevationShadow },
      }),
    };
  }

  if (variant === 'tonal') {
    const t = chipTonalTokens[tone];
    return {
      backgroundColor: t.bg,
      borderColor: t.border,
      color: t.text,
      ...(isInteractive && {
        '&:hover:not(:disabled)': { borderColor: t.text, boxShadow: chipElevationShadow },
      }),
    };
  }

  if (variant === 'outline') {
    const t = chipOutlineTokens[color];
    return {
      backgroundColor: surfaceTokens.paper,
      borderColor: t.border,
      color: t.text,
      ...(isInteractive && {
        '&:hover:not(:disabled)': {
          borderColor: t.text,
          boxShadow: chipElevationShadow,
          transform: 'translateY(-1px)',
        },
      }),
    };
  }

  if (variant === 'status') {
    return {
      backgroundColor: surfaceTokens.paper,
      borderColor: gray[200],
      color: gray[800],
      pl: '11px',
      ...(isInteractive && {
        '&:hover:not(:disabled)': { borderColor: controlTokens.cobalt },
      }),
    };
  }

  return {
    backgroundColor: surfaceTokens.paper,
    borderColor: gray[200],
    color: gray[700],
    height: '32px',
    pl: '4px',
    pr: '12px',
    ...(isInteractive && {
      '&:hover:not(:disabled)': {
        borderColor: gray[300],
        boxShadow: chipElevationShadow,
      },
    }),
  };
};

export const chipSelectedSx = (selected: boolean, isAdd: boolean) => {
  if (!selected || isAdd) return {};
  // Signal primary (Dark Blue), like every other selected control; never black.
  return {
    backgroundColor: controlTokens.primary,
    backgroundImage: 'none',
    borderColor: controlTokens.primary,
    color: surfaceTokens.paper,
  };
};
